import './select-switch.scss';

/**
 * Enhances a `<select>` element to behave as a navigation switch.
 * When the selected option value is a URL, the component redirects the browser
 * to that location on change.
 *
 * This used to mark the region switch overlay as dismissed on every change,
 * which also fired for the footer's plain language switch. The overlay is
 * decided in the backend now and remembers its own dismissal per session
 * (see language-switch.js), so there is nothing to suppress from here.
 */
class SelectSwitch {
    constructor(element) {
        // Defaults
        this.defaults = {
            initAttr: 'data-selectswitch'
        };

        // Elements
        this.$root = element;
        this.select = this.$root.querySelector('select');

        // Remember the current selection so it can be restored on a failed switch.
        this.previousValue = this.select.value;
    }

    /**
     * Initializes the component by wiring up all required event listeners.
     *
     * @returns {void} Does not return a value.
     */
    initialize() {
        this.setEvents();
    }

    /**
     * Registers DOM event listeners for the component.
     * Specifically, it listens to the `<select>` change event and navigates to
     * the selected value when it is present.
     *
     * @returns {void} Does not return a value.
     */
    setEvents() {
        this.select.addEventListener('change', (event) => {
            const value = event.target.value;

            // Only act when the selected option provides a non-empty value.
            if (!value) return;

            const postAction = this.$root.dataset.postAction;

            if (postAction) {
                const postParam = this.$root.dataset.postParam || 'value';
                const selectedOption = event.target.options[event.target.selectedIndex];
                const redirectUrl = selectedOption.dataset.redirectUrl;

                this.select.disabled = true;

                fetch(postAction, {
                    method: 'POST',
                    headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                    body: encodeURIComponent(postParam) + '=' + encodeURIComponent(value)
                })
                    .then(response => response.json())
                    .then((data) => {
                        if (data.success) {
                            window.location.href = redirectUrl || window.location.href;
                            return;
                        }

                        // Server rejected the switch: revert the selection and re-enable.
                        console.info('Switch not successful');
                        this.select.value = this.previousValue;
                        this.select.disabled = false;
                    })
                    .catch((error) => {
                        console.error('Error:', error);
                        this.select.value = this.previousValue;
                        this.select.disabled = false;
                    });
            } else {
                window.location.href = value;
            }
        });
    }
}

export { SelectSwitch };

window.addEventListener('content.loaded', (e) => {
    const eventDetails = e.detail;
    const $context = eventDetails.$context;

    if ($context) {
        const $$roots = $context.querySelectorAll('[data-selectswitch="root"]');
        $$roots.forEach(($root) => {
            const $rootAPI = new SelectSwitch($root);
            $rootAPI.initialize();
        });
    }
});
